import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

// Context
import { TimekeepingContext } from "../../context/domain/timekeeping/TimekeepingContext";
import { CoreContext } from "../../context/global/CoreContext";

// Hooks
import { useSignatureCanvas, useTimesheetSubmission } from "../../hooks/timekeeping";

// API calls
import {
  saveWorkerTimesheet,
  getWorkerTimesheetWeekEnding,
} from "../../services/api/timekeeping";

// UI components
import { BasicCard, Button } from "../../components/ui";
import { ModalWrapper } from "../../components/ui";

// Page specific features
import Title from "../../features/timekeeping/workerTimesheet/Title";
import WorkerShifts from "../../features/timekeeping/workerTimesheet/WorkerShifts";
import SafetyMeeting from "../../features/timekeeping/workerTimesheet/SafetyMeeting";
import Checklist from "../../features/timekeeping/workerTimesheet/Checklist";

/**
 * Page where the worker reviews their timesheet for the current week. This provides
};

/**
 * Page where the worker reviews their timesheet for the current week. This provides
 * the last chance for them to edit their shifts. They agree and sign off on the
 * hours worked here.
 *
 * Page saves the workers timesheet to the database upon submission (for manager review)
 * and redirects to the weekly schedule page.
 *
 * @returns The worker timesheet page.
 */
export default function WorkerTimesheet() {
  const { user, appName } = useContext(CoreContext);
  const { completedShifts, setDate, weekEnd, fetchWeeklyShifts } =
    useContext(TimekeepingContext);
  const [isSafetyMeetingReviewed, setIsSafetyMeetingReviewed] = useState(false);
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  // Signature canvas hook
  const {
    signatureRef,
    signatureContainerRef,
    isSigned,
    clearSignature,
    getSignatureData,
    validateSignature,
    signatureCanvasOptions,
    signatureCanvasCanvasProps,
    onSignatureEnd,
  } = useSignatureCanvas();

  // Timesheet submission hook
  const {
    isSubmitting,
    showSuccessModal,
    handleSubmit,
    closeSuccessModal,
  } = useTimesheetSubmission({
    submitFunction: saveWorkerTimesheet,
    onSuccess: () => {},
    onError: (error) => {
      alert(error);
    },
  });

  // Force and update of the weekly shifts data when the page loads, as it can be loaded
  // from another timekeeping page which still has the data in context, but not alwasy
  // updated.
  useEffect(() => {
    /**
     * Refetches weekly shifts so this page is not stale after other timekeeping screens.
     *
     * @returns {Promise<void>}
     */
    async function updateWeeklyShifts() {
      await fetchWeeklyShifts();
    }
    updateWeeklyShifts();
  }, [fetchWeeklyShifts]);

  // Set page title
  useEffect(() => {
    document.title = "Submit Timesheet";
    appName.current = "WorkerTimesheet";
    /**
     * Loads the worker timesheet week-ending date from the API.
     *
     * @returns {Promise<void>}
     */
    async function fetchDate() {
      const response = await getWorkerTimesheetWeekEnding();
      setDate(response["week_ending"]);
    }
    fetchDate();
  }, [appName, setDate]);

  // Submit timesheet function reads from the context which is updated by the shifts
  // component when user edits their shifts.
  /**
   * Validates the worker signature and submits completed shifts.
   *
   * @returns {void}
   */
  const handleSubmitTimesheet = () => {
    // Validate signature
    const validation = validateSignature();
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    // Get signature data
    const signatureData = getSignatureData();

    const dataToSubmit = {
      weekEnd: weekEnd,
      shifts: completedShifts,
      signature: signatureData,
    };

    handleSubmit(dataToSubmit);
  };

  /**
   * Closes the success modal and returns to the worker schedule.
   *
   * @returns {void}
   */
  const handleSuccessModalClose = () => {
    closeSuccessModal();
    navigate("/timekeeping/worker-schedule");
  };

  useEffect(() => {
    if (isSigned && isSafetyMeetingReviewed && completedShifts.length > 0 && !isEditing && !isSubmitting) {
      setIsSubmittable(true);
    } else {
      setIsSubmittable(false);
    }
  }, [isSigned, isSafetyMeetingReviewed, completedShifts, isEditing, isSubmitting]);

  return (
    <div className="max-w-[1024px] mx-auto px-2 md:px-4 mb-96 font-inter caret-transparent bg-custom-rsbg flex flex-col items-center justify-center p-4">
      <BasicCard
        bg="bg-gray-200"
        text="Please check your timesheet details below:"
        className="font-bold w-full mb-4 mt-2 p-2"
      />
      <Title user={user} className="mb-4" />
      <WorkerShifts isEditing={isEditing} setIsEditing={setIsEditing} />

      {/* Safety Meeting Review */}
      <SafetyMeeting
        weekEnd={weekEnd}
        isReviewed={isSafetyMeetingReviewed}
        setIsReviewed={setIsSafetyMeetingReviewed}
      />

      <BasicCard
        bg="bg-gray-200"
        text="I certify that the hours recorded on this timesheet are true and complete, and that all time I worked is accurately recorded. I received all required meal and rest breaks, or I promptly reported any issues to my Supervisor. I have reported any work-related injuries or illnesses."
        className="font-semibold text-sm w-full m-4 p-2"
      />

      {/* Signature Canvas */}
      <div className="w-full mb-4">
        <div
          className="border border-gray-300 overflow-hidden"
          ref={signatureContainerRef}
        >
          <div className="border-b font-bold border-gray-300 bg-gray-200 p-2 text-center">
            Signature
          </div>

          <div className="flex flex-col items-center">
            <SignatureCanvas
              ref={signatureRef}
              options={signatureCanvasOptions}
              onEnd={onSignatureEnd}
              canvasProps={signatureCanvasCanvasProps}
            />
          </div>
        </div>
        <div className="flex justify-center">
          <Button
            text="Clear"
            variant={isSigned ? "warning" : "disabled"}
            className="w-full rounded-t-none"
            onClick={clearSignature}
          />
        </div>
      </div>

      <Checklist
        className="w-full"
        shifts={completedShifts.length > 0}
        signed={isSigned}
        saved={!isEditing}
        safetyMeetingReviewed={isSafetyMeetingReviewed}
      />
      <Button
        text={
          isSubmitting
            ? ""
            : showSuccessModal
              ? "Submitted"
              : "Submit Timesheet"
        }
        icon={isSubmitting ? "spinner" : undefined}
        variant={
          showSuccessModal ? "disabled" : isSubmittable ? "primary" : "disabled"
        }
        className="mb-4 w-full"
        onClick={handleSubmitTimesheet}
      />

      <ModalWrapper isOpen={showSuccessModal} onClose={handleSuccessModalClose}>
        <div className="text-center p-4">
          <div className="mb-4">
            <div className="text-green-600 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Timesheet Submitted Successfully!
            </h2>
            <p className="text-gray-600 mb-4">
              Thank you, your manager has been notified and will review your
              timesheet.
            </p>
            <p className="text-gray-600 mb-6">
              You will be returned to your weekly schedule when you press OK.
            </p>
          </div>
          <Button
            text="OK"
            variant="primary"
            className="w-full"
            onClick={handleSuccessModalClose}
          />
        </div>
      </ModalWrapper>
    </div>
  );
}
